import {useEffect, useMemo} from 'react';
import {Button, Form, Select, Tooltip} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {fieldRules} from 'utils/helper';
import {CONTEXT_ENTITY_TYPES, CONTEXT_STEP_TYPE_LABEL} from './constants';
import {FilterForm} from './FilterForm';
import styles from './FlowBuilder.module.scss';

const humanize = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const entityOptions = CONTEXT_ENTITY_TYPES.map(v => ({
  value: v,
  label: humanize(v),
}));

const stepTypeOption = stepType => ({
  value: stepType,
  label: CONTEXT_STEP_TYPE_LABEL[stepType] || humanize(stepType),
});

/**
 * One context-fetch step inside the Issue editor's Form.List.
 *
 * Enforces the five cascading invariants from flow-builder.md §3.11:
 *   1. resolver dropdown disabled until a key is picked
 *   2. resolvers filtered to those whose applicableKeys include the key
 *   3. type auto-snaps to the resolver's stepTypes (disabled when only one)
 *   4. resolver description + __typename shown inline
 *   5. changing key clears the resolver if it no longer applies
 */
export const ContextStep = ({
  field,
  index,
  form,
  catalog = [],
  catalogLoading,
  onRemove,
}) => {
  const {key, name, ...rest} = field;

  // Watch this step's own values so cascade rules can react.
  const selectedKey = Form.useWatch(['contextConfig', name, 'key'], form);
  const selectedResolver = Form.useWatch(
    ['contextConfig', name, 'resolver'],
    form,
  );

  const resolverByValue = useMemo(
    () => Object.fromEntries(catalog.map(r => [r.resolver, r])),
    [catalog],
  );

  const activeResolver = selectedResolver
    ? resolverByValue[selectedResolver]
    : null;

  const applicableResolvers = useMemo(() => {
    if (!selectedKey) return [];
    return catalog.filter(r =>
      Array.isArray(r.applicableKeys)
        ? r.applicableKeys.includes(selectedKey)
        : false,
    );
  }, [catalog, selectedKey]);

  // Invariant 5: if the picked resolver no longer applies to the new key, clear it.
  // (Also re-checks once the catalog finishes loading.)
  useEffect(() => {
    if (!selectedKey || !selectedResolver) return;
    if (!applicableResolvers.length) return; // catalog not loaded for this key yet
    const stillApplies = applicableResolvers.some(
      r => r.resolver === selectedResolver,
    );
    if (!stillApplies) {
      form.setFields([
        {name: ['contextConfig', name, 'resolver'], value: undefined},
        {name: ['contextConfig', name, 'type'], value: undefined},
      ]);
    }
  }, [selectedKey, applicableResolvers, selectedResolver, form, name]);

  // Invariant 3: snap type to the resolver's stepTypes.
  // Re-runs whenever the resolver OR the catalog (which determines stepTypes)
  // becomes available, so a pre-loaded resolver still gets its type stamped
  // once the catalog arrives.
  useEffect(() => {
    if (!activeResolver) return;
    const allowed = activeResolver.stepTypes || [];
    if (!allowed.length) return;
    const current = form.getFieldValue(['contextConfig', name, 'type']);
    if (allowed.length === 1) {
      if (current !== allowed[0]) {
        form.setFields([
          {name: ['contextConfig', name, 'type'], value: allowed[0]},
        ]);
      }
    } else if (current && !allowed.includes(current)) {
      form.setFields([
        {name: ['contextConfig', name, 'type'], value: undefined},
      ]);
    }
  }, [activeResolver, form, name]);

  const resolverDisabled = !selectedKey || catalogLoading;
  const resolverPlaceholder = !selectedKey
    ? '— pick a key first —'
    : catalogLoading
    ? 'Loading resolvers…'
    : applicableResolvers.length === 0
    ? `— no resolvers wired for ${selectedKey} yet —`
    : 'Select resolver';

  const resolverOptions = applicableResolvers.map(r => ({
    value: r.resolver,
    label: humanize(r.resolver),
  }));

  const allowedStepTypes = activeResolver?.stepTypes || [];
  const typeDisabled = !activeResolver || allowedStepTypes.length <= 1;
  const typeOptions = allowedStepTypes.map(stepTypeOption);

  return (
    <div key={key} className={styles.contextStep}>
      <div className={styles.contextStepHeader}>
        <span className={styles.stepNumber}>Step {index + 1}</span>
        <Tooltip title="Remove step">
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Tooltip>
      </div>

      <div className={styles.fieldGrid}>
        <Form.Item
          {...rest}
          name={[name, 'key']}
          label="Feature"
          rules={[fieldRules('Entity key')]}>
          <Select
            showSearch
            placeholder="Pick entity"
            options={entityOptions}
          />
        </Form.Item>

        <Form.Item
          {...rest}
          name={[name, 'resolver']}
          label="Resolver"
          rules={[fieldRules('Resolver')]}>
          <Select
            placeholder={resolverPlaceholder}
            options={resolverOptions}
            disabled={resolverDisabled}
            loading={catalogLoading}
            showSearch
            optionFilterProp="label"
            notFoundContent={
              selectedKey
                ? `No resolvers wired for ${selectedKey}`
                : 'Pick a key first'
            }
          />
        </Form.Item>

        <Form.Item
          {...rest}
          name={[name, 'type']}
          label="Type"
          rules={[fieldRules('Type')]}>
          <Select
            placeholder={
              activeResolver ? 'Select type' : 'Pick a resolver first'
            }
            options={typeOptions}
            disabled={typeDisabled}
          />
        </Form.Item>

        <Form.Item
          {...rest}
          name={[name, 'dependsOn']}
          label="Depends on a Related Feature (optional)"
          tooltip="Key of a previous step to chain off">
          <Select allowClear placeholder="None" options={entityOptions} />
        </Form.Item>
      </div>

      {activeResolver ? (
        <div className={styles.resolverHint}>
          <div className={styles.resolverHintHeader}>
            <span className={styles.resolverHintTypename}>
              {activeResolver.typename}
            </span>
            {allowedStepTypes.length === 1 ? (
              <span className={styles.resolverHintLocked}>
                type locked to <strong>{humanize(allowedStepTypes[0])}</strong>
              </span>
            ) : null}
          </div>
          {activeResolver.description ? (
            <p className={styles.resolverHintDescription}>
              {activeResolver.description}
            </p>
          ) : null}
        </div>
      ) : null}

      {activeResolver ? (
        <>
          <div className={styles.filterFormHeader}>
            <span className={styles.filterFormTitle}>Filters</span>
            <span className={styles.filterFormSubtitle}>
              All optional — leave blank to skip a dimension.
            </span>
          </div>
          <Form.Item
            {...rest}
            name={[name, 'payload']}
            style={{marginBottom: 0}}>
            <FilterForm descriptor={activeResolver} />
          </Form.Item>
        </>
      ) : (
        <div className={styles.filterFormEmpty}>
          Pick a resolver to configure its filters.
        </div>
      )}
    </div>
  );
};
